import { Injectable } from "@angular/core";
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest
} from "@angular/common/http";
import { AuthService } from "./auth.service";
import { Observable } from "rxjs";
import { catchError, mergeMap, take } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import * as Sentry from "@sentry/browser";

@Injectable({
    providedIn: "root"
})
export class JwtInterceptor implements HttpInterceptor {
    // HTTP statuses that are part of the normal app flow and would only add noise
    // to Sentry. NOTE: status 0 ("Unknown Error" / network failure) is intentionally
    // NOT here — that is a real error we want to track.
    private readonly ignoredSentryStatuses = [401];

    constructor(public auth: AuthService) {}

    intercept(
        req: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        // Timestamp before the request starts, so we can report how long it ran before
        // failing. ~0ms = connection refused/CORS; ~30s = timeout (server up but slow).
        const startTime = Date.now();

        return this.auth.token$.pipe(
            take(1),
            mergeMap(token => {
                let headers = req.headers;
                if (token) {
                    headers = req.headers.append(
                        "Authorization",
                        `Bearer ${token}`
                    );
                }
                if (
                    !req.url.startsWith("/assets") &&
                    !req.url.startsWith("https://") &&
                    !req.url.startsWith("http://")
                ) {
                    const url = `${environment.apiBaseUrl}/${req.url}`;
                    const apiReq = req.clone({
                        url,
                        headers
                    });
                    return next.handle(apiReq);
                }
                return next.handle(req);
            }),
            catchError((err: any) => {
                // Only HTTP failures are captured here (with context), BEFORE they can be
                // swallowed by a downstream subscribe()/catchError. This covers the
                // "status 0 / Unknown Error" cases that never reach Angular's ErrorHandler.
                // Non-HTTP errors in the stream fall through to Angular's ErrorHandler /
                // global handlers, so they are not double-captured or mislabeled here.
                if (err instanceof HttpErrorResponse) {
                    // Skip statuses that are part of normal app flow (e.g. 401 token
                    // expiry) to avoid flooding Sentry. status 0 is NOT skipped.
                    if (!this.ignoredSentryStatuses.includes(err.status)) {
                        // HttpErrorResponse is NOT an Error instance, so passing it directly
                        // to captureException makes Sentry synthesize a stack at the capture
                        // site (issues then show up grouped as "Hub.withScope"). Wrap it in a
                        // real Error so the title, stack trace and grouping are meaningful.
                        const sentryError = new Error(
                            err.message || `HTTP ${err.status} ${req.method} ${err.url}`
                        );
                        sentryError.name = "HttpError";

                        // Normalize the URL for grouping: drop the query string and replace
                        // ids (Mongo ObjectId / numeric) so paginated/per-id requests don't
                        // fragment the same error into many separate Sentry issues.
                        const groupingUrl = (err.url || req.url || "")
                            .split("?")[0]
                            .replace(/\/[0-9a-f]{24}(?=\/|$)/gi, "/:id")
                            .replace(/\/\d+(?=\/|$)/g, "/:id");

                        Sentry.withScope(scope => {
                            scope.setTag("error.source", "http");
                            scope.setTag("http.status", String(err.status));
                            scope.setExtra("url", err.url);
                            scope.setExtra("method", req.method);
                            scope.setExtra("status", err.status);
                            scope.setExtra("statusText", err.statusText);
                            if (typeof err.error === "string") {
                                scope.setExtra("body", err.error);
                            }

                            // Diagnostic signals captured at failure time, so a client
                            // incident can be understood from Sentry alone (the user will
                            // never open DevTools). The browser hides the raw net::ERR_*
                            // reason from JS, so we approximate it with these:
                            //  - offline           -> connectivity loss (the user's network)
                            //  - durationMs ~0      -> connection refused / CORS / DNS
                            //  - durationMs ~30000  -> timeout (server up but not responding)
                            scope.setTag("offline", String(!navigator.onLine));
                            scope.setExtra("onLine", navigator.onLine);
                            scope.setExtra("durationMs", Date.now() - startTime);
                            const conn = (navigator as any).connection;
                            if (conn && conn.effectiveType) {
                                scope.setExtra("connectionType", conn.effectiveType);
                            }
                            scope.setFingerprint([
                                "http",
                                req.method,
                                String(err.status),
                                groupingUrl
                            ]);
                            Sentry.captureException(sentryError);
                        });
                    }

                    // Logout side-effect must run regardless of whether we report to Sentry.
                    if (err.status === 401) {
                        this.auth.clearLocalStorage();
                    }
                }

                throw err;
            })
        );
    }
}
